/**
 * WHO READ YOU, ONE SEALED MONTH AT A TIME - `getAudienceBreakdown` on the Statistics screen.
 *
 * Register R-020 (lens findings C3-08, U1-06). The platform computes and seals each author's month
 * of readers - star sign, age, country, platform - and no screen showed it. The page design put it
 * here, on Statistics, with its own month picker, because "Audience is not a date range - it is one
 * sealed month, one dimension per call" (docs/ui/04-pages.md, 3.3c).
 *
 * WHAT IT DRAWS, AND THE THREE THINGS IT MUST NOT:
 *   - ONE DIMENSION AT A TIME, one bar per group, in the order the platform sends them.
 *   - THE MERGED SMALL GROUPS ARE THEIR OWN BAR. Groups under the floor were summed at sealing into
 *     one `below_threshold` line - "a real bucket code, not an absence" - and it is drawn as a bar
 *     like the others, named for what it is: "7 smaller countries". Its reader count is printed only
 *     when the platform sends one, which it does only when the line merged two or more groups
 *     (decision-log row 219, item 3); otherwise the line says there were too few to show.
 *   - EACH GROUP'S SHARE, AND A DONUT (the owner, 2026-10-06, decision-log row 269: "what is the
 *     problem in privacy to show it like a donut % and number" - this replaces row 219's "no total
 *     and no percentage"). A share is of the lines on screen (./statistics/audience-shares.ts); the
 *     small groups' line now always carries its number ("Show its number"), and one sent without it
 *     is left out of the whole, said in words. There is still no "unknown" group for readers who
 *     told us nothing: each reader counts only in the charts they gave an answer for. A bar's
 *     LENGTH is drawn against the largest bar on screen.
 *   - "NOT SEALED YET" IS A STATE, NOT AN ERROR. The open month, and a month the seal has not
 *     reached, answer 404 `MONTH_NOT_SEALED`; that is said in words, never drawn as zero.
 *
 * THE DEFAULT MONTH comes from the server's day (./audience-months.ts): this app has no clock. That
 * day is `getDashboard`'s `to`, read with no range by ./ArticleStatsRoute.tsx and handed in - one
 * read for this tab and the Overview tab's periods (design 15).
 * EMPTY AT LAUNCH, HONESTLY: "its denominator is the reader-attribute route and no shipped client
 * calls it yet" (authors-api.yaml, `getAudienceBreakdown`), so a sealed month with no groups says
 * that nobody told us, rather than drawing an empty chart.
 */
import { Group, Paper, SegmentedControl, Stack, Text, Title } from '@mantine/core';
import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { PeriodPicker } from '@moonx/ui';
import { hasErrorCode, useCabinetApi } from '../../features/cabinet/api';
import type { AudienceBreakdown, AudienceDimension, Dashboard } from '../../features/cabinet/api';
import { EmptyState, ErrorState, LoadingState } from '../../features/cabinet/components';
import type { AsyncResult } from '../../features/cabinet/state';
import { formatCount } from '../../features/cabinet/format';
import { monthLabel } from '../../features/calendar/domain/month';
import type { MonthKey } from '../../features/calendar/domain/month';
import { useTranslation } from '../../i18n';
import type { ArticlesT } from './articles-i18n';
import { audienceMonths, firstDayOf } from './audience-months';
import type { AudienceMonths } from './audience-months';
import { AudienceDonut } from './statistics/AudienceDonut';
import { shareText, sharesOf } from './statistics/audience-shares';
import type { AudienceShares } from './statistics/audience-shares';

/*
 * Each dimension's words are the catalogue's `stats.audience.dimensions.<value>`: its picker word,
 * and the two sentences that name its groups.
 */

/** The first in the contract's order, and the one the panel opens on. */
const STAR_SIGN: AudienceDimension = 'zodiac';

/** The four dimensions in the contract's own order; a traffic source is not one of them. */
const DIMENSIONS: readonly AudienceDimension[] = [
  STAR_SIGN,
  'age_band',
  'country',
  // ★ "Device" (design 15): the bucket labels are "iOS app", "Android app", "Web".
  'platform',
];

function known(dimension: AudienceDimension): AudienceDimension {
  return DIMENSIONS.find((each) => each === dimension) ?? STAR_SIGN;
}

/** A group's size in words, in readers (design 23, R15): the audience is everyone who engaged. */
function people(count: number, t: ArticlesT): string {
  return t('stats.audience.readers', { count, value: formatCount(count) });
}

/** Bars, or the donut (decision-log row 269). Bars first: they compare twelve groups best. */
type View = 'bars' | 'donut';

const VIEWS: readonly View[] = ['bars', 'donut'];

export interface AudiencePanelProps {
  /**
   * `getDashboard` with no range: the server's most recent completed day, the only "now" this app
   * has, resolved on the server clock. Nothing else of that response is read here.
   */
  readonly serverDay: AsyncResult<Dashboard>;
  /** Where the month button goes - the tabs' row; `null` while the tab is not shown. */
  readonly pickerSlot?: HTMLElement | null | undefined;
}

export function AudiencePanel({ serverDay: day, pickerSlot }: AudiencePanelProps) {
  const { t } = useTranslation('articles');
  const headingId = useId();
  const months = day.data === undefined ? undefined : audienceMonths(day.data.to);

  return (
    <Stack gap="md" component="section" aria-labelledby={headingId}>
      <Stack gap={4}>
        <Title order={2} size="h3" id={headingId}>
          {t('stats.audience.title')}
        </Title>
        <Text size="sm" c="dimmed">
          {t('stats.audience.intro')}
        </Text>
      </Stack>

      {day.status === 'error' && day.data === undefined ? (
        <ErrorState
          title={t('stats.audience.monthsDidNotLoad')}
          error={day.error}
          onRetry={day.refetch}
        />
      ) : day.data === undefined ? (
        <LoadingState label={t('stats.audience.findingMonths')} />
      ) : months === undefined ? (
        <EmptyState
          headline={t('stats.audience.noMonthsTitle')}
          explanation={t('stats.audience.noMonthsText')}
        />
      ) : (
        <Breakdown months={months} pickerSlot={pickerSlot} />
      )}
    </Stack>
  );
}

interface Outcome {
  readonly key: string;
  readonly body?: AudienceBreakdown;
  readonly error?: unknown;
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function Breakdown({
  months,
  pickerSlot,
}: {
  readonly months: AudienceMonths;
  readonly pickerSlot: AudiencePanelProps['pickerSlot'];
}) {
  const { t } = useTranslation('articles');
  const api = useCabinetApi();
  // "Last month" - the newest ended month - until another is picked (design 24, T15b).
  const [picked, setPicked] = useState<MonthKey | 'last'>('last');
  const month = picked === 'last' ? months.ended[0] : picked;
  const [dimension, setDimension] = useState<AudienceDimension>(STAR_SIGN);
  const [view, setView] = useState<View>('bars');
  const [attempt, setAttempt] = useState(0);
  // ★ KEYED ON WHAT WAS ASKED. An answer is shown only for the month and dimension it answers:
  // switching the month must never leave the last month's bars - or its "not sealed" - under the
  // new month's name for even one frame.
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const monthStart = firstDayOf(month);
  const key = `${monthStart}:${dimension}:${String(attempt)}`;

  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    api.getAudienceBreakdown({ month: monthStart, dimension }, controller.signal).then(
      (body) => {
        if (live) {
          setOutcome({ key, body });
        }
      },
      (error: unknown) => {
        if (live && !isAbort(error)) {
          setOutcome({ key, error });
        }
      },
    );
    return () => {
      live = false;
      controller.abort();
    };
  }, [api, key, monthStart, dimension]);

  const settled = outcome !== null && outcome.key === key ? outcome : null;
  // Newest first: the open month, then the ended ones. The default is the newest ENDED month.
  const options = [
    {
      value: months.open,
      label: t('stats.audience.stillOpen', { month: monthLabel(months.open) }),
    },
    ...months.ended.map((ended) => ({ value: ended, label: monthLabel(ended) })),
  ];
  // The Statistics period button's look and place, months only: a month is sealed whole, and months
  // do not add up into a range (design 24, T15b), so there is no custom range here.
  const picker = (
    <PeriodPicker
      mode="months"
      label={t('stats.audience.month')}
      presets={[
        { value: 'last', label: t('stats.audience.lastMonth'), month: monthLabel(months.ended[0]) },
      ]}
      months={options.map((option) => ({ ...option, month: monthLabel(option.value) }))}
      value={picked}
      onChange={setPicked}
    />
  );

  return (
    <Paper p="lg">
      <Stack gap="md">
        <Group gap="md" align="flex-end" wrap="wrap">
          {pickerSlot === undefined ? picker : pickerSlot && createPortal(picker, pickerSlot)}
          <SegmentedControl
            aria-label={t('stats.audience.groupBy')}
            value={dimension}
            onChange={(value) => {
              setDimension(known(value as AudienceDimension));
            }}
            data={DIMENSIONS.map((value) => ({
              value,
              label: t(`stats.audience.dimensions.${value}.label`),
            }))}
          />
          <SegmentedControl
            aria-label={t('stats.audience.drawAs')}
            value={view}
            onChange={(value) => {
              setView(value === 'donut' ? 'donut' : 'bars');
            }}
            data={VIEWS.map((value) => ({ value, label: t(`stats.audience.views.${value}`) }))}
          />
        </Group>

        {settled === null ? (
          <LoadingState label={t('stats.audience.readingMonth', { month: monthLabel(month) })} />
        ) : settled.body !== undefined ? (
          <Chart body={settled.body} month={month} dimension={dimension} view={view} />
        ) : hasErrorCode(settled.error, 'MONTH_NOT_SEALED') ? (
          <Stack gap={4} role="status">
            <Text size="sm" fw={700}>
              {month === months.open
                ? t('stats.audience.stillCounted', { month: monthLabel(month) })
                : t('stats.audience.nothingFor', { month: monthLabel(month) })}
            </Text>
            <Text size="sm" c="dimmed">
              {month === months.open
                ? t('stats.audience.showsWhenOver')
                : t('stats.audience.notCounted')}
            </Text>
          </Stack>
        ) : (
          <ErrorState
            title={t('stats.audience.monthDidNotLoad', { month: monthLabel(month) })}
            error={settled.error}
            onRetry={() => {
              setAttempt((current) => current + 1);
            }}
          />
        )}
      </Stack>
    </Paper>
  );
}

interface ChartProps {
  readonly body: AudienceBreakdown;
  readonly month: MonthKey;
  readonly dimension: AudienceDimension;
  readonly view: View;
}

function Chart({ body, month, dimension, view }: ChartProps) {
  const { t } = useTranslation('articles');
  const merged = body.suppressed;

  if (body.buckets.length === 0 && merged === undefined) {
    return (
      <EmptyState
        headline={t('stats.audience.nothingFor', { month: monthLabel(month) })}
        explanation={t('stats.audience.emptyText')}
      />
    );
  }

  // With no merged line this label is never drawn, so its count does not matter there.
  const smaller = merged === undefined ? 1 : merged.bucketCount;
  const shares = sharesOf(
    body,
    t(`stats.audience.dimensions.${dimension}.smaller`, {
      count: smaller,
      value: formatCount(smaller),
    }),
  );
  const listLabel = t(`stats.audience.dimensions.${dimension}.readersBy`, {
    month: monthLabel(month),
  });

  return (
    <Stack gap="xs">
      {view === 'donut' ? (
        <AudienceDonut shares={shares} listLabel={listLabel} people={(count) => people(count, t)} />
      ) : (
        <Bars shares={shares} listLabel={listLabel} />
      )}
      {merged === undefined ? null : (
        <Text size="xs" c="dimmed">
          {t('stats.audience.smallTogether')}
          {shares.partial ? t('stats.audience.partial') : ''}
        </Text>
      )}
    </Stack>
  );
}

function Bars({
  shares,
  listLabel,
}: {
  readonly shares: AudienceShares;
  readonly listLabel: string;
}) {
  // The longest bar is the largest group ON SCREEN - a scale for drawing.
  const largest = Math.max(1, ...shares.slices.map((slice) => slice.count ?? 0));
  return (
    <Stack
      gap={6}
      component="ul"
      aria-label={listLabel}
      style={{ listStyle: 'none', margin: 0, padding: 0 }}
    >
      {shares.slices.map((slice) => (
        <BarRow
          key={slice.key}
          label={slice.label}
          count={slice.count}
          share={slice.count === null ? null : shareText(slice.count, shares.total)}
          largest={largest}
          merged={slice.merged}
        />
      ))}
    </Stack>
  );
}

interface BarRowProps {
  readonly label: string;
  /** `null`: the merged line whose number the platform withholds. */
  readonly count: number | null;
  /** "11%" of the lines on screen; `null` beside a line with no number. */
  readonly share: string | null;
  readonly largest: number;
  readonly merged: boolean;
}

function BarRow({ label, count, share, largest, merged }: BarRowProps) {
  const { t } = useTranslation('articles');
  const length = count === null ? 0 : Math.min(1, count / largest);
  return (
    <li>
      <Group gap="sm" wrap="nowrap" align="center">
        <Text
          size="sm"
          style={{ flex: '0 1 11rem', minWidth: 0, overflowWrap: 'anywhere' }}
          fs={merged ? 'italic' : undefined}
        >
          {label}
        </Text>
        {/* The bar is drawing only: its number is the text beside it. */}
        <div
          aria-hidden="true"
          style={{
            flex: '1 1 4rem',
            height: '0.75rem',
            borderRadius: 4,
            backgroundColor: 'oklch(var(--mx-surface2))',
            border: merged ? '1px dashed oklch(var(--mx-border2))' : undefined,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${String(length * 100)}%`,
              height: '100%',
              backgroundColor: merged
                ? 'oklch(var(--mx-accent) / 0.45)'
                : 'oklch(var(--mx-accent))',
            }}
          />
        </div>
        <Text
          size="sm"
          style={{
            flex: '0 0 auto',
            minWidth: '6.5rem',
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
          }}
          {...(count === null ? { c: 'dimmed' } : {})}
        >
          {count === null ? t('stats.audience.tooFew') : people(count, t)}
        </Text>
        {share === null ? (
          <span style={{ flex: '0 0 3rem' }} />
        ) : (
          <Text
            size="sm"
            c="dimmed"
            style={{ flex: '0 0 3rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
          >
            {share}
          </Text>
        )}
      </Group>
    </li>
  );
}

/*
 * ★ DESIGN 15 (the owner's live look at real cabinets, 2026-10-01). The panel is the "Audience" tab
 * of Statistics now (./ArticleStatsRoute.tsx), its picker and bars in a card as the prototype's
 * audience block is; the "platform" dimension reads "Device", and no sentence names the platform.
 * A bar row gives way on a phone - the group's name wraps, the bar shrinks, the count keeps its
 * words - instead of pushing the page sideways. Nothing it reads, or refuses to show, moved.
 */

/*
 * ★ PLAIN WORDS (the owner, 2026-10-06: "not overcomplicate - thats dahsboard for user, not tech
 * documentation"). The intro says what the tab is and when a month shows; an empty month says there
 * is nothing to show and where it comes from; a merged line says only why small groups are together.
 * What the panel draws and refuses to draw is unchanged: no total, no percentage, no "unknown" group,
 * the merged line's count only when the platform sends it. The sealing time is no longer printed.
 */

/*
 * ★ THE MONTH BUTTON (design 24, T15b; the owner, 2026-10-11: "can't we add a similar picker there?",
 * option 1). The month select became Statistics' period button in its months-only mode, in the same
 * place - the tabs' row (./ArticleStatsRoute.tsx) - with "Last month" first, then the months as
 * before: the open one, named as open, and the 24 ended. It is still one sealed month: a reader is
 * counted once a month, and groups merged under the privacy floor month by month do not add up, so
 * the button offers no range. Real ranges here would be a design of their own, with a server read.
 */
